import React from 'react';
import { Spin } from 'antd';
import type { DashboardDimension } from '../../types';
import { GAS_OVERVIEW_INDICATORS, WATER_OVERVIEW_INDICATORS } from '../../constants';
import { useOverviewData, type OverviewData } from '../../hooks/useOverviewData';
import gasIcon from '@/assets/icons/supply-selected@2x.webp';
import waterIcon from '@/assets/icons/water-selected@2x.webp';
import styles from './index.module.scss';
import { formatThousandUnitDefaultTwo } from '@/utils/utils';

interface EmissionOverviewProps {
  dimension: DashboardDimension;
  timeValue: string;
  refreshKey: number;
}

const GAS_VALUE_KEYS: Record<string, keyof OverviewData['gas']> = {
  gasEmission: 'gasEmission' as keyof OverviewData['gas'],
  nox: 'nox' as keyof OverviewData['gas'],
  nmhc: 'nmhc' as keyof OverviewData['gas'],
};

const GAS_UNIT_KEYS: Record<string, keyof OverviewData['gas']> = {
  gasEmission: 'gasEmissionUnit' as keyof OverviewData['gas'],
  nox: 'noxUnit' as keyof OverviewData['gas'],
  nmhc: 'nmhcUnit' as keyof OverviewData['gas'],
};

const WATER_VALUE_KEYS: Record<string, keyof OverviewData['water']> = {
  nh3nEmission: 'nh3nEmission' as keyof OverviewData['water'],
  tpEmission: 'tpEmission' as keyof OverviewData['water'],
  codEmission: 'codEmission' as keyof OverviewData['water'],
  tnEmission: 'tnEmission' as keyof OverviewData['water'],
  waterEmission: 'waterEmission' as keyof OverviewData['water'],
};

const WATER_UNIT_KEYS: Record<string, keyof OverviewData['water']> = {
  nh3nEmission: 'nh3nUnit' as keyof OverviewData['water'],
  tpEmission: 'tpUnit' as keyof OverviewData['water'],
  codEmission: 'codUnit' as keyof OverviewData['water'],
  tnEmission: 'tnUnit' as keyof OverviewData['water'],
  waterEmission: 'waterEmissionUnit' as keyof OverviewData['water'],
};

const renderIndicatorCard = (key: string, name: string, unit?: string, value?: number, actualUnit?: string) => (
  <div className={styles.indicatorItem} key={key}>
    <span className={styles.name}>{name}</span>
    <span>
      <span className={styles.value}>{formatThousandUnitDefaultTwo(value)}</span>
      <span className={styles.unit}>{actualUnit || unit}</span>
    </span>
  </div>
);

const EmissionOverview: React.FC<EmissionOverviewProps> = ({ dimension, timeValue, refreshKey }) => {
  const { data, loading } = useOverviewData(dimension, timeValue, refreshKey);

  return (
    <Spin spinning={loading}>
      <div className={styles.section}>
        {/* 废气排放 panel */}
        <div className={styles.panel} style={{ width: 494, flex: 'none' }}>
          <div className={styles.panelHeader}>
            <img src={gasIcon} className={styles.panelIcon} alt="" />
            <span className={styles.panelTitle}>废气</span>
          </div>
          <div className={styles.indicatorCard}>
            {GAS_OVERVIEW_INDICATORS.map(indicator => {
              const valueKey = GAS_VALUE_KEYS[indicator.key];
              const unitKey = GAS_UNIT_KEYS[indicator.key];
              const value = data?.gas?.[valueKey];
              const actualUnit = data?.gas?.[unitKey] ?? indicator.unit;
              return renderIndicatorCard(indicator.key, indicator.name, indicator.unit, value, actualUnit);
            })}
          </div>
        </div>

        {/* 废水排放 panel */}
        <div className={styles.panel} style={{ width: 832 }}>
          <div className={styles.panelHeader}>
            <img src={waterIcon} className={styles.panelIcon} alt="" />

            <span className={styles.panelTitle}>废水</span>
          </div>
          <div className={styles.indicatorCard}>
            {WATER_OVERVIEW_INDICATORS.map(indicator => {
              const valueKey = WATER_VALUE_KEYS[indicator.key];
              const unitKey = WATER_UNIT_KEYS[indicator.key];
              const value = data?.water?.[valueKey];
              const actualUnit = data?.water?.[unitKey] ?? indicator.unit;
              return renderIndicatorCard(indicator.key, indicator.name, indicator.unit, value, actualUnit);
            })}
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default EmissionOverview;
